import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { WebSocketNotification } from '../hooks/useWebSocket';
import { subscribeToNotifications, getNotifications, clearAllNotifications } from '../hooks/useNotification';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../hooks/useAuth';

/**
 * 알림 아이콘 반환
 */
const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'NEW_SAMPLE_REQUEST':
      return '📋';
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
      return '📝';
    case 'SAMPLE_REQUEST_UPDATED':
      return '✏️';
    case 'NEW_FEEDBACK':
      return '💬';
    case 'FEEDBACK_STATUS_CHANGE':
      return '💡';
    case 'NEW_FEEDBACK_REPLY':
      return '💌';
    case 'PROJECT_UPDATE':
      return '📁';
    case 'CONTRACT_REQUEST':
      return '📄';
    case 'CONTRACT_SIGNED_BY_BRAND':
      return '✍️';
    case 'CONTRACT_COMPLETED':
      return '✅';
    default:
      return '🔔';
  }
};

/**
 * 알림 타입에 따른 네비게이션 경로 반환
 */
const getNotificationPath = (notification: WebSocketNotification, userType: string | null): string | null => {
  const data = notification.data || {};

  switch (notification.type) {
    case 'NEW_SAMPLE_REQUEST':
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
    case 'SAMPLE_REQUEST_UPDATED':
      if (data.requestId) {
        // projectId 없는 standalone 요청은 공용 라우터(/requests/:id)가 admin/brand 상세를 해소.
        if (!data.projectId) {
          return `/requests/${data.requestId}`;
        }
        return userType === 'admin'
          ? `/admin/management?tab=projects&projectId=${data.projectId}&requestId=${data.requestId}`
          : `/sample-request?tab=projects&project=${data.projectId}&request=${data.requestId}`;
      }
      break;
    case 'NEW_FEEDBACK':
    case 'FEEDBACK_STATUS_CHANGE':
    case 'NEW_FEEDBACK_REPLY':
      if (data.feedbackId) {
        if (userType === 'admin') {
          return `/admin/feedbacks/${data.feedbackId}`;
        } else {
          return `/brand/feedbacks/${data.feedbackId}`;
        }
      }
      break;
    case 'PROJECT_UPDATE':
      if (data.projectId) {
        if (userType === 'admin') {
          return `/admin/management?tab=projects&projectId=${data.projectId}`;
        } else {
          return `/sample-request?tab=projects&project=${data.projectId}`;
        }
      }
      break;
    case 'QUOTATION_UPDATE':
    case 'NEW_QUOTATION':
      if (data.quotationId) {
        if (userType === 'admin') {
          return `/admin/quotations/${data.quotationId}`;
        }
      }
      break;
    case 'CONTRACT_REQUEST':
    case 'CONTRACT_COMPLETED':
      if (userType === 'brand') {
        // Brand user - navigate to contracts tab in dashboard
        return '/sample-request?tab=contracts';
      } else if (userType === 'admin') {
        // Admin user - navigate to contracts management
        if (data.contractId) {
          return `/admin/management?tab=contracts&contractId=${data.contractId}`;
        }
        return '/admin/management?tab=contracts';
      }
      break;
    case 'CONTRACT_SIGNED_BY_BRAND':
      // Admin user - navigate to contracts in admin management with specific contract
      if (userType === 'admin') {
        if (data.contractId) {
          return `/admin/management?tab=contracts&contractId=${data.contractId}`;
        }
        return '/admin/management?tab=contracts';
      }
      break;
  }

  return null;
};

/**
 * 알림 벨 컴포넌트
 *
 * WebSocket을 통해 실시간 알림을 수신하고 표시합니다.
 */
const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState<WebSocketNotification[]>(() =>
    getNotifications() as WebSocketNotification[]
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { userType } = useAuth();

  // useRealtimeSync handles both WebSocket notifications AND React Query invalidation
  const {
    connectionStatus,
    notifications: wsNotifications,
    unreadCount: wsUnreadCount,
    markAsRead,
    clearNotifications: clearWsNotifications,
  } = useRealtimeSync();

  // Subscribe to local notifications
  useEffect(() => {
    const unsubscribe = subscribeToNotifications((notifications) => {
      setLocalNotifications(notifications as WebSocketNotification[]);
    });
    return unsubscribe;
  }, []);

  // Merge WebSocket and local notifications, sorted by timestamp
  const notifications = useMemo(() => {
    const merged = [...wsNotifications, ...localNotifications];
    // Sort by timestamp (newest first) and deduplicate
    const seen = new Set<string>();
    return merged
      .sort((a, b) => {
        const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
        const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
        return timeB - timeA;
      })
      .filter(n => {
        const key = `${n.type}-${n.message}-${n.timestamp}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 50);
  }, [wsNotifications, localNotifications]);

  // Count unread notifications
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !(n as any).read).length;
  }, [notifications]);

  // Clear all notifications (both WS and local)
  const clearNotifications = () => {
    clearWsNotifications();
    clearAllNotifications();
  };

  // 알림 클릭 시 해당 페이지로 이동
  const handleNotificationClick = (notification: WebSocketNotification, index: number) => {
    const path = getNotificationPath(notification, userType);
    markAsRead(index);
    setIsOpen(false);
    if (path) {
      navigate(path);
    }
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 드롭다운 열 때 읽음 처리
  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      markAsRead();
    }
  };

  // 연결 상태 색상
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500 animate-pulse';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 알림 벨 버튼 */}
      <button
        onClick={handleToggle}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        title={`Notifications (${connectionStatus})`}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* 미읽은 알림 배지 */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* 연결 상태 표시 */}
        <span
          className={`absolute bottom-1 right-1 w-2 h-2 rounded-full ${getConnectionStatusColor()}`}
          title={`Connection: ${connectionStatus}`}
        />
      </button>

      {/* 알림 드롭다운 */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-hidden bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Notifications</h3>
            {notifications.length > 0 && (
              <button
                onClick={clearNotifications}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>

          {/* 알림 목록 */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <svg
                  className="w-12 h-12 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {notifications.map((notification, index) => (
                  <NotificationItem
                    key={`${notification.timestamp}-${index}`}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification, index)}
                    hasNavigation={!!getNotificationPath(notification, userType)}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* 푸터 - 연결 상태 */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center text-xs text-gray-500">
              <span className={`w-2 h-2 rounded-full mr-2 ${getConnectionStatusColor()}`} />
              <span>
                {connectionStatus === 'connected'
                  ? 'Real-time updates active'
                  : connectionStatus === 'connecting'
                  ? 'Connecting...'
                  : connectionStatus === 'error'
                  ? 'Connection error'
                  : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 개별 알림 아이템 컴포넌트
 */
interface NotificationItemProps {
  notification: WebSocketNotification;
  onClick?: () => void;
  hasNavigation?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  hasNavigation = false,
}) => {
  const timeAgo = React.useMemo(() => {
    try {
      const ts = typeof notification.timestamp === 'number'
        ? new Date(notification.timestamp)
        : new Date(notification.timestamp);
      return formatDistanceToNow(ts, { addSuffix: true });
    } catch {
      return '';
    }
  }, [notification.timestamp]);

  const isRead = (notification as any).read;

  return (
    <li
      className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
        !isRead ? 'bg-blue-50' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <span className="text-lg flex-shrink-0">
          {getNotificationIcon(notification.type)}
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm line-clamp-2 ${!isRead ? 'text-gray-900 font-medium' : 'text-gray-800'}`}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-gray-400">
              {timeAgo}
            </p>
            {hasNavigation && (
              <span className="text-xs text-np-600 font-medium">
                View details →
              </span>
            )}
          </div>
        </div>
        {!isRead && (
          <span className="w-2 h-2 bg-np-500 rounded-full flex-shrink-0 mt-1.5"></span>
        )}
      </div>
    </li>
  );
};

export default NotificationBell;
